import React, { useMemo, useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  IconButton,
  Typography,
} from '@rentacenter/racstrap';
import { KeyboardArrowUp, KeyboardArrowDown } from '@material-ui/icons';
import { customerStyles } from '../../customerStyles';
import { useCustomerServiceState } from '../../../context/CustomerService/CustomerServiceProvider';
import { ActivityItem, Attachments, TicketNote } from '../../../types/types';
import { gridStyle, parseBusinessDuration } from '../../shared/utils';

const formatDateTime = (dt: string) => {
  try {
    const d = new Date(dt);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dt;
  }
};

const getNoteLabel = (noteType?: string) => {
  switch (noteType) {
    case 'CMNTS':
      return 'Comments';
    case 'WN':
      return 'Work Notes';
    case 'CLSD':
      return 'Closed Notes';
    case 'RCA':
      return 'Resolution Notes';
    case 'RJCT':
      return 'Reject Notes';
    default:
      return 'Comments';
  }
};

const Activity: React.FC = () => {
  const classes = customerStyles();
  const { incident } = useCustomerServiceState();
  const businessDuration = incident.activity.businessDuration ?? '00:00:00:00';

  const activities: ActivityItem[] = useMemo(
    () => incident?.activity?.activities ?? [],
    [incident?.activity?.activities]
  );

  const ticketNotes: TicketNote[] = useMemo(
    () => incident?.activity?.ticketNotes ?? [],
    [incident?.activity?.ticketNotes]
  );

  const basicInfoAttachments: Attachments[] = useMemo(
    () => incident?.basicInfo?.attachments ?? [],
    [incident?.basicInfo?.attachments]
  );

  const [expanded, setExpanded] = useState(true);
  const toggle = () => setExpanded((x) => !x);

  const noteIndex = useMemo(() => {
    const map = new Map<string, TicketNote>();
    ticketNotes.forEach((n) => map.set(n.ticketNoteId, n));
    return map;
  }, [ticketNotes]);

  const attachmentIndex = useMemo(() => {
    const map = new Map<string, Attachments>();
    basicInfoAttachments.forEach((n) => map.set(n.attachmentId ?? '', n));
    return map;
  }, [basicInfoAttachments]);

  const items = useMemo(() => {
    return [...activities]
      .map((a) => ({
        ...a,
        note: a.ticketNoteId ? noteIndex.get(a.ticketNoteId) : undefined,
        attachment: a.attachmentId
          ? attachmentIndex.get(a.attachmentId)
          : undefined,
      }))
      .sort(
        (a, b) =>
          new Date(b.activityDate).getTime() -
          new Date(a.activityDate).getTime()
      );
  }, [activities, noteIndex, attachmentIndex]);

  const { display: totalTimeWorked } = parseBusinessDuration(businessDuration);

  return (
    <Grid container id="resolinfo" className={`${classes.mb16} ${classes.px2}`}>
      <Grid item md={12}>
        <Card className={`${classes.card} ${classes.w100}`}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item md={6}>
                <Typography className={classes.title}>
                  Activity Information
                </Typography>
              </Grid>

              <Grid
                item
                md={6}
                container
                justifyContent="flex-end"
                alignItems="flex-start"
              >
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{
                    marginRight: 8,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <span className={classes.noteLabel}>Total time worked:</span>
                  &nbsp;
                  <span className={classes.rightNoteContent}>
                    {totalTimeWorked}
                  </span>
                </Typography>

                <IconButton
                  onClick={toggle}
                  size="small"
                  aria-label="toggle-activity-panel"
                >
                  {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </IconButton>
              </Grid>
            </Grid>

            {expanded && (
              <Grid container spacing={2}>
                {items.length === 0 && (
                  <Grid item xs={12}>
                    <Typography color="textSecondary">
                      No activities to display.
                    </Typography>
                  </Grid>
                )}

                {items.map((act) => {
                  const {
                    activityId,
                    activityDate,
                    activityBy,
                    statusType,
                    statusDescEn,
                    resolvedBy,
                    assignedTo,
                    note,
                    attachment,
                  } = act;

                  const statusText = statusDescEn || statusType || '';
                  const hasNote = Boolean(note);
                  const hasAttachment = Boolean(attachment);

                  return (
                    <Grid item xs={12} key={activityId}>
                      <div className={classes.activityDivider}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={3} style={gridStyle}>
                            <Typography
                              variant="body2"
                              className={classes.leftTime}
                            >
                              {`Time: ${formatDateTime(activityDate)}`}
                            </Typography>

                            {activityBy && (
                              <Typography
                                variant="body2"
                                className={classes.leftMeta}
                              >
                                <span className={classes.noteLabel}>
                                  Created by:
                                </span>{' '}
                                <span className={classes.rightNoteContent}>
                                  {activityBy}
                                </span>
                              </Typography>
                            )}

                            {hasNote && resolvedBy && (
                              <Typography
                                variant="body2"
                                className={classes.leftMetaDense}
                              >
                                <span className={classes.noteLabel}>
                                  Resolved by:
                                </span>{' '}
                                <span className={classes.rightNoteContent}>
                                  {resolvedBy}
                                </span>
                              </Typography>
                            )}

                            {hasNote && assignedTo && (
                              <Typography
                                variant="body2"
                                className={classes.leftMetaDense}
                              >
                                <span className={classes.noteLabel}>
                                  Assigned to:
                                </span>{' '}
                                <span className={classes.rightNoteContent}>
                                  {assignedTo}
                                </span>
                              </Typography>
                            )}

                            {statusText && (
                              <Typography
                                variant="body2"
                                className={classes.leftStatus}
                              >
                                Status: {statusText}
                              </Typography>
                            )}
                          </Grid>

                          <Grid item xs={12} md={9} style={gridStyle}>
                            {hasNote && note && (
                              <div className={classes.rightNoteWrap}>
                                <Typography
                                  variant="body2"
                                  component="div"
                                  className={classes.rightNoteText}
                                >
                                  <span className={classes.rightNoteLabel}>
                                    {getNoteLabel(note.noteType)}:
                                  </span>{' '}
                                  <span className={classes.rightNoteContent}>
                                    {note.notes}
                                  </span>
                                </Typography>
                              </div>
                            )}

                            {!hasNote && (resolvedBy || assignedTo) && (
                              <div className={classes.rightNoNoteWrap}>
                                {resolvedBy && (
                                  <Typography
                                    className={classes.rightNoNoteResolved}
                                  >
                                    <span className={classes.rightNoteLabel}>
                                      Resolved by:
                                    </span>{' '}
                                    <span className={classes.rightNoteContent}>
                                      {resolvedBy}
                                    </span>
                                  </Typography>
                                )}
                                {assignedTo && (
                                  <Typography
                                    variant="body2"
                                    className={classes.rightNoNoteAssigned}
                                  >
                                    <span className={classes.rightNoteLabel}>
                                      Assigned to:
                                    </span>{' '}
                                    <span className={classes.rightNoteContent}>
                                      {assignedTo}
                                    </span>
                                  </Typography>
                                )}
                              </div>
                            )}

                            {hasAttachment && attachment && (
                              <Typography
                                variant="body2"
                                component="div"
                                className={classes.rightNoteText}
                                style={{ marginTop: 4 }}
                              >
                                <span className={classes.rightNoteLabel}>
                                  Attachment:
                                </span>{' '}
                                <span className={classes.rightNoteContent}>
                                  {attachment.attachmentName}
                                </span>
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </div>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default Activity;
